import React from 'react';

import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';

import LoadingPage from '../LoadingPage';
import ConfirmationPage from './ConfirmationPage';
import LandlinePage from './LandlinePage';
import PhoneNumberPage from './PhoneNumberPage';
import { FraudAlertRouterQuery as FraudAlertRouterQueryType } from 'src/clients/Network/gql/build/FraudAlertRouterQuery.graphql';
import { trackPageView } from 'src/clients/tracking';
import { useGlobalStore } from 'src/state/global';

const FraudAlertRouterQuery = graphql`
  query FraudAlertRouterQuery {
    authIdentity {
      id
      creditApplications {
        id
        creditReport {
          entity {
            fraudAlertPhoneHint
          }
        }
      }
    }
  }
`;

export type FraudAlertPageType = 'PHONE_NUMBER' | 'CONFIRMATION' | 'LANDLINE';

const FraudAlertRouter = () => {
  const data = useLazyLoadQuery<FraudAlertRouterQueryType>(FraudAlertRouterQuery, {}, { fetchPolicy: 'network-only' });
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const [currentPage, setCurrentPage] = React.useState<FraudAlertPageType>('PHONE_NUMBER');
  const [e164, setE164] = React.useState<string | undefined>(undefined);
  const fraudAlertLast4 = data.authIdentity.creditApplications.find((ca) => ca.id === creditApplicationId)?.creditReport
    ?.entity?.fraudAlertPhoneHint;

  React.useEffect(() => {
    switch (currentPage) {
      case 'CONFIRMATION':
        trackPageView('FraudAlertConfirmation');
        break;
      case 'LANDLINE':
        trackPageView('FraudAlertLandline');
        break;
      default:
        break;
    }
  }, [currentPage]);

  const onConfirmationNext = React.useCallback((e164: string) => {
    setE164(e164);
    setCurrentPage('CONFIRMATION');
  }, []);

  const onLandlineNext = React.useCallback(() => {
    setCurrentPage('LANDLINE');
  }, []);

  return (
    <>
      {currentPage === 'PHONE_NUMBER' && (
        <PhoneNumberPage last4={fraudAlertLast4 ?? undefined} onNext={onConfirmationNext} />
      )}
      {currentPage === 'CONFIRMATION' && <ConfirmationPage e164={e164} onLandlineNext={onLandlineNext} />}
      {currentPage === 'LANDLINE' && <LandlinePage e164={e164} />}
    </>
  );
};

const WithSuspense = () => {
  return (
    <React.Suspense fallback={<LoadingPage />}>
      <FraudAlertRouter />
    </React.Suspense>
  );
};

export default WithSuspense;
